import { useEffect, useState } from 'react';
import { useStore } from 'zustand';

import { SearchResultTab } from './search-result-tab.component';

import { AllSearchResults, SearchCategoryResults, SearchResult, SearchType } from '@/app/(main)/_components/search-modal/search-modal.types';
import { fetchResultsByType } from '@/app/(main)/_components/search-modal/_services/search-modal-results.service';
import { setFetchedResultsByType, useSearchState } from '@/store/search/search.store';
import { SelectedProcedures } from '@/app/(main)/_components/search-modal/search-modal.types';
import { OverridedUserLocation } from '@/utils/formatters/city/format-place-from-slug.const';
import { getLogger } from '@/logger/logger';
import { InfiniteScroll } from '@/components/ui/infinite-scroll.component';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { SearchModalListEnum } from '@/types/enums/analytics/home/home-analytics.enum';
import { useFeatureFlag } from '@/feature-flags/hooks/use-feature-flag.hook';
import { RESULTS_PAGE_SIZE } from '@/app/(main)/_components/search-modal/_consts/search-results.consts';
import { useSearchSource } from '@/app/(main)/_components/search-modal/_hooks/use-search-source.hook';

interface SearchResultsProps {
  results: SearchCategoryResults;
  searchType: SearchType;
  searchTerm: string;
  selectLaboratoryProcedure?: (procedure: SelectedProcedures) => void;
  setOpen: (open: boolean) => void;
  locationOverride?: OverridedUserLocation;
}

export const SearchResults = ({
  results,
  searchType,
  searchTerm,
  setOpen,
  selectLaboratoryProcedure,
  locationOverride,
}: SearchResultsProps) => {
  const fetchedResults = useStore(useSearchState, (state) => state?.fetchedResults);
  const { value: newSearchModalDesign } = useFeatureFlag('newSearchModalDesign');
  const { source, citySlug } = useSearchSource();

  const chooseInitialDisplayedResults = () => {
    if (fetchedResults) {
      return fetchedResults[searchType as keyof AllSearchResults].results;
    }

    return results.results;
  };

  const chooseInitialPage = () => {
    if (fetchedResults) {
      return Math.ceil(fetchedResults[searchType as keyof AllSearchResults].results.length / RESULTS_PAGE_SIZE);
    }

    return 1;
  };

  const chooseInitialHasMore = () => {
    if (fetchedResults) {
      return fetchedResults[searchType as keyof AllSearchResults].hasMore;
    }

    return results.hasMore;
  };

  const [displayedResults, setDisplayedResults] = useState<SearchResult[]>(chooseInitialDisplayedResults());
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(chooseInitialPage());
  const [hasMore, setHasMore] = useState(chooseInitialHasMore());

  const fetchPage = (pageNumber: number): Promise<SearchCategoryResults> =>
    fetchResultsByType({
      term: searchTerm,
      searchType,
      page: pageNumber,
      limit: RESULTS_PAGE_SIZE,
      source,
      citySlug,
    });

  const loadMoreResults = async () => {
    if (isLoading || !hasMore) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetchPage(page + 1);

      setDisplayedResults((prev) => [...prev, ...response.results]);
      setFetchedResultsByType(searchType, [...displayedResults, ...response.results], response.hasMore);
      setPage((prevPage) => prevPage + 1);
      setHasMore(response.hasMore);
    } catch (error) {
      const logger = getLogger('SearchModal - SearchResults');
      logger.error('Error loading more results', {
        search: { type: searchType, term: searchTerm, page },
        error,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadFirstFullPage = async () => {
    setIsLoading(true);
    try {
      const response = await fetchPage(1);

      setDisplayedResults(response.results);
      setFetchedResultsByType(searchType, response.results, response.hasMore);
      setPage(1);
      setHasMore(response.hasMore);
    } catch (error) {
      const logger = getLogger('SearchModal - SearchResults');
      logger.error('Error loading the first page of results', {
        search: { type: searchType, term: searchTerm },
        error,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const seedIsAPartialPage
      = displayedResults.length < RESULTS_PAGE_SIZE && results.hasMore;

    if (seedIsAPartialPage) {
      loadFirstFullPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      {displayedResults.map((result: SearchResult, index: number) => (
        <div
          key={`${result.id}${result.displayName}-${index}`}
          className="hover:bg-gray-50 transition-colors"
        >
          <SearchResultTab
            {...result}
            variant={newSearchModalDesign ? 'compact' : undefined}
            locationOverride={locationOverride}
            searchTerm={searchTerm}
            list={SearchModalListEnum.RESULTS}
            position={index + 1}
            totalResults={displayedResults.length}
            setOpen={setOpen}
            selectLaboratoryProcedure={selectLaboratoryProcedure}
          />
          <hr />
        </div>
      ))}

      <InfiniteScroll
        hasMore={hasMore}
        isLoading={isLoading}
        onLoadMore={loadMoreResults}
        data-analytics-event={AnalyticsEnum.SHOW_MORE_RESULTS_PROCEDURES_MODAL}
      />
    </div>
  );
};
